import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  Component,
  DestroyRef,
  OnDestroy,
  PLATFORM_ID,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { DsButtonDirective, DsCardDirective, DsIconComponent, DsInputDirective } from '../ui';
import { Meta, Title } from '@angular/platform-browser';
import { CONTENT_CATEGORY_ORDER } from './content-category-order';
import { CONTENT_UI, ContentUiKey, localizedContentMap } from './localized-content-pages';
import { LOCALIZED_CONTENT_LANGUAGES } from './content-route-manifest.generated';
import { LANGUAGE_DIRECTIONS } from '../core/supported-language-codes.generated';
import { LocalizationService } from '../core/localization.service';
import { PUBLIC_SITE_ORIGIN, publicSiteUrl } from '../core/public-url';
import {
  CONTENT_LAST_REVIEWED,
  CONTENT_PAGE_MAP,
  CONTENT_PAGES,
  ContentPage,
  contentUrl,
} from './content-pages';

@Component({
  selector: 'app-content-page',
  imports: [RouterLink, DsButtonDirective, DsCardDirective, DsIconComponent, DsInputDirective],
  templateUrl: './content-page.html',
  styleUrl: './content-page.scss',
})
export class ContentPageComponent implements OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly i18n = inject(LocalizationService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private reactionTimer?: ReturnType<typeof setTimeout>;
  private memoryTimer?: ReturnType<typeof setTimeout>;
  private reactionReadyAt = 0;

  protected readonly language = signal('en');
  protected readonly pages = signal<ContentPage[]>(CONTENT_PAGES);
  protected readonly pageQuery = signal('');
  protected readonly selectedCategory = signal('all');
  protected readonly resultLimit = signal(24);
  private readonly pageMap = signal<Map<string, ContentPage>>(CONTENT_PAGE_MAP);
  protected readonly categoryChoices = computed(() => {
    const pages = this.pages();
    return CONTENT_CATEGORY_ORDER.map((category) => ({
      category,
      count: pages.filter((page) => this.categoryFor(page) === category).length,
    })).filter((choice) => choice.count > 0);
  });
  protected readonly filteredContentPages = computed(() => {
    const query = this.normalizeSearchText(this.pageQuery());
    const terms = query.split(/\s+/u).filter(Boolean);
    const category = this.selectedCategory();

    return this.pages()
      .filter((page) => category === 'all' || this.categoryFor(page) === category)
      .filter((page) => {
        if (terms.length === 0) return true;
        const haystack = this.normalizeSearchText(
          [
            page.title,
            page.description,
            page.summary,
            this.categoryFor(page),
            ...page.sections.map((section) => section.heading),
            ...page.faqs.map((faq) => faq.question),
          ].join(' '),
        );
        return terms.every((term) => haystack.includes(term));
      })
      .sort((left, right) => {
        const score = (page: ContentPage) => {
          const title = this.normalizeSearchText(page.title);
          if (!query) return 0;
          if (title.startsWith(query)) return 0;
          if (title.includes(query)) return 1;
          if (this.normalizeSearchText(this.categoryFor(page)).includes(query)) return 2;
          return 3;
        };
        return score(left) - score(right) || left.title.localeCompare(right.title);
      });
  });
  protected readonly visibleContentPages = computed(() =>
    this.filteredContentPages().slice(0, this.resultLimit()),
  );
  protected readonly hasMoreContent = computed(
    () => this.visibleContentPages().length < this.filteredContentPages().length,
  );
  protected readonly page = signal<ContentPage | null>(null);
  protected readonly isIndex = signal(false);
  protected readonly lastReviewed = CONTENT_LAST_REVIEWED;
  protected readonly contentBasePath = computed(() => `/${this.language()}/content/`);
  protected readonly popularContentPages = computed(() => {
    const preferred = [
      'what-is-iq',
      'what-is-the-average-iq',
      'iq-score-ranges-explained',
      'iq-percentile-calculator',
      'can-you-improve-your-iq',
      'is-an-online-iq-test-accurate',
    ];
    return preferred
      .map((slug) => this.pageMap().get(slug) ?? CONTENT_PAGE_MAP.get(slug))
      .filter((page): page is ContentPage => page !== undefined)
      .slice(0, 6);
  });
  protected readonly recentContentPages = computed(() => [...this.pages()].slice(-6).reverse());
  protected readonly recommendedContentPages = computed(() => {
    const selected: ContentPage[] = [];
    for (const category of CONTENT_CATEGORY_ORDER) {
      const page = this.pages().find(
        (candidate) =>
          this.categoryFor(candidate) === category &&
          !selected.some((item) => item.slug === candidate.slug),
      );
      if (page) selected.push(page);
      if (selected.length === 6) break;
    }
    return selected;
  });

  protected readonly firstValue = signal(100);
  protected readonly secondValue = signal(20);
  protected readonly toolResult = signal('');
  protected readonly reactionState = signal<'idle' | 'waiting' | 'ready'>('idle');
  protected readonly memorySequence = signal('');
  protected readonly memoryVisible = signal(false);
  protected readonly memoryAnswer = signal('');

  constructor() {
    this.route.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      const language = (data['contentLanguage'] as string | undefined) ?? 'en';
      const map = language === 'en' ? CONTENT_PAGE_MAP : localizedContentMap(language);
      const pages = language === 'en' ? CONTENT_PAGES : Array.from(map.values());
      const slug = data['contentSlug'] as string | undefined;
      const page = slug ? (map.get(slug) ?? null) : null;

      this.language.set(language);
      this.pageMap.set(map);
      this.pages.set(pages);
      this.page.set(page);
      this.isIndex.set(!slug);
      this.pageQuery.set('');
      this.selectedCategory.set('all');
      this.resultLimit.set(24);
      this.resetTool();
      this.applySeo(page, language);
      if (this.isBrowser) {
        void this.i18n.setLanguage(language).then(() => this.applySeo(page, language));
      }
    });

    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const requested = params.get('topic');
      const category = CONTENT_CATEGORY_ORDER.includes(requested ?? '') ? requested! : 'all';
      this.selectedCategory.set(category);
      this.resultLimit.set(24);
    });
  }

  ngOnDestroy(): void {
    if (this.reactionTimer) clearTimeout(this.reactionTimer);
    if (this.memoryTimer) clearTimeout(this.memoryTimer);
  }

  protected urlFor(slug: string): string {
    const language = this.pageMap().has(slug) ? this.language() : 'en';
    return contentUrl(slug, language);
  }

  protected ct(key: ContentUiKey): string {
    return CONTENT_UI[this.language()]?.[key] ?? CONTENT_UI['en'][key] ?? key;
  }

  protected pagePath(): string {
    const page = this.page();
    return page ? contentUrl(page.slug, this.language()) : this.contentBasePath();
  }

  protected canonicalUrl(): string {
    return publicSiteUrl(this.pagePath());
  }

  protected shareUrl(network: 'linkedin' | 'facebook' | 'x' | 'email'): string {
    const url = encodeURIComponent(this.canonicalUrl());
    const title = encodeURIComponent(this.page()?.title ?? this.ct('knowledgeCenter'));
    if (network === 'linkedin') return `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
    if (network === 'facebook') return `https://www.facebook.com/sharer/sharer.php?u=${url}`;
    if (network === 'x') return `https://twitter.com/intent/tweet?url=${url}&text=${title}`;
    return `mailto:?subject=${title}&body=${title}%0A%0A${url}`;
  }

  protected async copyArticleLink(): Promise<void> {
    if (!this.isBrowser || !navigator.clipboard) return;
    await navigator.clipboard.writeText(this.canonicalUrl());
  }

  protected tocLabel(heading: string): string {
    if (heading.length <= 52) return heading;
    const shortened = heading.slice(0, 49).replace(/\s+\S*$/u, '');
    return shortened + '…';
  }

  protected cardLinkLabel(page: ContentPage): string {
    const title = page.title.length > 48 ? this.tocLabel(page.title) : page.title;
    return `${page.tool ? 'Open' : 'Read'}: ${title}`;
  }

  protected setContentQuery(value: string): void {
    this.pageQuery.set(value);
    this.resultLimit.set(24);
  }

  protected selectContentCategory(category: string): void {
    this.selectedCategory.set(category);
    this.resultLimit.set(24);
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { topic: category === 'all' ? null : category },
      queryParamsHandling: 'merge',
      fragment: 'directory-heading',
    });
  }

  protected clearContentFilters(): void {
    this.pageQuery.set('');
    this.selectedCategory.set('all');
    this.resultLimit.set(24);
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { topic: null },
      queryParamsHandling: 'merge',
      fragment: 'directory-heading',
    });
  }

  protected showMoreContent(): void {
    this.resultLimit.update((limit) => limit + 24);
  }

  protected hasActiveContentFilters(): boolean {
    return this.pageQuery().trim().length > 0 || this.selectedCategory() !== 'all';
  }

  protected relatedPages(page: ContentPage): ContentPage[] {
    return page.related
      .map((slug) => this.pageMap().get(slug) ?? CONTENT_PAGE_MAP.get(slug))
      .filter((candidate): candidate is ContentPage => candidate !== undefined);
  }

  protected setFirst(value: string): void {
    this.firstValue.set(Number(value));
  }

  protected setSecond(value: string): void {
    this.secondValue.set(Number(value));
  }

  protected setMemoryAnswer(value: string): void {
    this.memoryAnswer.set(value.replace(/\D/g, ''));
  }

  protected calculate(tool: ContentPage['tool']): void {
    if (tool === 'percentile') {
      const score = Math.min(200, Math.max(40, this.firstValue()));
      const percentile = this.normalCdf((score - 100) / 15) * 100;
      const shown =
        percentile > 99.99 ? '>99.99' : percentile < 0.01 ? '<0.01' : percentile.toFixed(2);
      this.toolResult.set(
        `An IQ score of ${score} is approximately at the ${shown} percentile on a mean-100, SD-15 normal model.`,
      );
      return;
    }
    if (tool === 'mental-age') {
      const iq = Math.max(1, this.firstValue());
      const age = Math.max(1, this.secondValue());
      this.toolResult.set(
        `Historical ratio formula result: mental age ≈ ${((iq / 100) * age).toFixed(1)} years. This outdated calculation is not how modern adult IQ tests are scored.`,
      );
    }
  }

  protected startReaction(): void {
    if (this.reactionTimer) clearTimeout(this.reactionTimer);
    this.toolResult.set('Wait for the panel to say GO, then press it as quickly as you can.');
    this.reactionState.set('waiting');
    this.reactionTimer = setTimeout(
      () => {
        this.reactionReadyAt = performance.now();
        this.reactionState.set('ready');
        this.toolResult.set('GO — press now.');
      },
      1200 + Math.random() * 1800,
    );
  }

  protected recordReaction(): void {
    if (this.reactionState() === 'waiting') {
      if (this.reactionTimer) clearTimeout(this.reactionTimer);
      this.reactionState.set('idle');
      this.toolResult.set('Too early. Start another trial and wait for GO.');
      return;
    }
    if (this.reactionState() === 'ready') {
      const milliseconds = Math.round(performance.now() - this.reactionReadyAt);
      this.reactionState.set('idle');
      this.toolResult.set(
        `${milliseconds} ms for this trial. Browser, display, input hardware, and anticipation affect the result.`,
      );
      return;
    }
    this.startReaction();
  }

  protected startMemory(): void {
    const length = Math.min(12, Math.max(4, Math.round(this.firstValue() || 6)));
    this.memorySequence.set(Array.from({ length }, () => Math.floor(Math.random() * 10)).join(''));
    this.memoryAnswer.set('');
    this.memoryVisible.set(true);
    this.toolResult.set(`Memorize the ${length}-digit sequence.`);
    if (this.memoryTimer) clearTimeout(this.memoryTimer);
    this.memoryTimer = setTimeout(() => {
      this.memoryVisible.set(false);
      this.toolResult.set('Enter the sequence from memory.');
    }, 2500);
  }

  protected checkMemory(): void {
    const correct = this.memoryAnswer() === this.memorySequence();
    this.toolResult.set(
      correct
        ? `Correct: you recalled ${this.memorySequence().length} digits in this trial.`
        : `Not this time. The sequence was ${this.memorySequence()}. Try a new sequence when ready.`,
    );
  }

  protected chooseToolAnswer(tool: ContentPage['tool'], value: number): void {
    const messages: Record<string, string[]> = {
      'brain-age': [
        'Your choices suggest a careful approach. “Brain age” is not a clinical construct; focus on specific skills rather than an age label.',
        'Your choices suggest a balanced speed-and-accuracy approach. This activity is not a biological or cognitive age estimate.',
        'Your choices suggest a fast exploratory approach. Device and strategy affect this result; it is not diagnostic.',
      ],
      'color-vision': [
        'You selected the lower-contrast option. Display calibration and lighting make this only a screen.',
        'You selected the middle-contrast option. A browser display cannot diagnose color-vision deficiency.',
        'You selected the higher-contrast option. Consult an eye-care professional if you have concerns.',
      ],
      'cognitive-style': [
        'You may prefer structured, sequential problem solving. Preferences are flexible, not ability levels.',
        'You may switch between structured and exploratory strategies depending on the task.',
        'You may prefer exploratory, big-picture strategies. Preferences are not fixed cognitive types.',
      ],
      'learning-style': [
        'You may value written structure. Mix reading with retrieval and application rather than matching one fixed “style.”',
        'You may value examples and discussion. Effective learning still benefits from recall and spaced practice.',
        'You may value hands-on practice. Preferences do not prove that one matching teaching mode is required.',
      ],
    };
    this.toolResult.set(
      messages[tool ?? '']?.[value] ?? 'Result recorded for this educational activity.',
    );
  }

  private categoryFor(page: ContentPage): string {
    return CONTENT_PAGE_MAP.get(page.slug)?.category ?? page.category ?? 'IQ essentials';
  }

  private normalizeSearchText(value: string): string {
    return value
      .normalize('NFKD')
      .replace(/\p{Diacritic}/gu, '')
      .toLocaleLowerCase()
      .trim();
  }

  private resetTool(): void {
    this.firstValue.set(100);
    this.secondValue.set(20);
    this.toolResult.set('');
    this.reactionState.set('idle');
    this.memorySequence.set('');
    this.memoryVisible.set(false);
    this.memoryAnswer.set('');
  }

  private normalCdf(z: number): number {
    const sign = z < 0 ? -1 : 1;
    const x = Math.abs(z) / Math.sqrt(2);
    const t = 1 / (1 + 0.3275911 * x);
    const erf =
      sign *
      (1 -
        ((((1.061405429 * t - 1.453152027) * t + 1.421413741) * t - 0.284496736) * t +
          0.254829592) *
          t *
          Math.exp(-x * x));
    return 0.5 * (1 + erf);
  }

  private applySeo(page: ContentPage | null, language: string): void {
    const title = page ? `${page.title} | IQLabTest` : `${this.ct('knowledgeCenter')} | IQLabTest`;
    const description = page?.description ?? this.ct('indexLead');
    const path = page ? contentUrl(page.slug, language) : `/${language}/content`;
    const canonical = publicSiteUrl(path);

    this.title.setTitle(title);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({
      name: 'robots',
      content: page ? 'index, follow, max-image-preview:large' : 'noindex, follow',
    });
    this.meta.updateTag({ property: 'og:type', content: page ? 'article' : 'website' });
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:url', content: canonical });
    this.meta.updateTag({ property: 'og:site_name', content: 'IQLabTest' });
    this.meta.updateTag({
      property: 'og:image',
      content: `${PUBLIC_SITE_ORIGIN}/assets/brand/iqlabtest-social-card.png`,
    });
    this.meta.updateTag({
      property: 'og:image:alt',
      content: `${page?.title ?? this.ct('knowledgeCenter')} — IQLabTest`,
    });
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: title });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({
      name: 'twitter:image',
      content: `${PUBLIC_SITE_ORIGIN}/assets/brand/iqlabtest-social-card.png`,
    });
    this.meta.updateTag({
      name: 'twitter:image:alt',
      content: `${page?.title ?? this.ct('knowledgeCenter')} — IQLabTest`,
    });
    this.document.documentElement.lang = language;
    this.document.documentElement.dir = LANGUAGE_DIRECTIONS[language] ?? 'ltr';
    this.upsertCanonical(canonical);
    this.upsertAlternates(page);
    this.upsertStructuredData(page, canonical, language);
  }

  private upsertCanonical(url: string): void {
    let link = this.document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.document.createElement('link');
      link.rel = 'canonical';
      this.document.head.appendChild(link);
    }
    link.href = url;
  }

  private upsertAlternates(page: ContentPage | null): void {
    this.document.head
      .querySelectorAll('link[data-iqlabtest-alternate]')
      .forEach((element) => element.remove());

    const slug = page?.slug;
    const languages = slug
      ? [
          'en',
          ...Array.from(
            new Set(
              LOCALIZED_CONTENT_LANGUAGES.filter((language) =>
                localizedContentMap(language).has(slug),
              ),
            ),
          ),
        ]
      : ['en', ...LOCALIZED_CONTENT_LANGUAGES];

    for (const language of languages) {
      const link = this.document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = language;
      link.href = publicSiteUrl(slug ? contentUrl(slug, language) : `/${language}/content`);
      link.setAttribute('data-iqlabtest-alternate', 'true');
      this.document.head.appendChild(link);
    }
    {
      const fallback = this.document.createElement('link');
      fallback.rel = 'alternate';
      fallback.hreflang = 'x-default';
      fallback.href = publicSiteUrl(slug ? contentUrl(slug, 'en') : '/en/content');
      fallback.setAttribute('data-iqlabtest-alternate', 'true');
      this.document.head.appendChild(fallback);
    }
  }
  private upsertStructuredData(
    page: ContentPage | null,
    canonical: string,
    language: string,
  ): void {
    this.document.getElementById('content-structured-data')?.remove();
    const script = this.document.createElement('script');
    script.id = 'content-structured-data';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(
      page
        ? {
            '@context': 'https://schema.org',
            '@graph': [
              {
                '@type': page.tool ? 'WebApplication' : 'Article',
                headline: page.title,
                name: page.title,
                description: page.description,
                inLanguage: language,
                mainEntityOfPage: canonical,
                url: canonical,
                applicationCategory: page.tool ? 'EducationalApplication' : undefined,
                dateModified: CONTENT_LAST_REVIEWED,
                author: { '@type': 'Organization', name: 'IQLabTest' },
                publisher: { '@type': 'Organization', name: 'IQLabTest', url: publicSiteUrl() },
              },
              {
                '@type': 'FAQPage',
                mainEntity: page.faqs.map((faq) => ({
                  '@type': 'Question',
                  name: faq.question,
                  acceptedAnswer: { '@type': 'Answer', text: faq.answer },
                })),
              },
            ],
          }
        : {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: this.ct('knowledgeCenter'),
            description: this.ct('indexLead'),
            url: canonical,
            inLanguage: language,
          },
    );
    this.document.head.appendChild(script);
  }
}
